import { useEffect, useState } from 'react';

import { Box, CircularProgress, Paper } from '@mui/material';
import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import OperationSubheader from 'src/components/OperationSubheader';
import { getDebenturesResumeData } from 'src/services/operation/operationDebentures';
import { getMarketDisplayLabel } from 'src/utils/markets';

import OperationDebenturesAsset from './OperationDebenturesAsset';
import OperationDebenturesGeneralData from './OperationDebenturesGeneralData';
import OperationDebenturesLiability from './OperationDebenturesLiability';
import OperationDebenturesResume from './OperationDebenturesResume';
import OperationDebenturesServiceProviders from './OperationDebenturesServiceProviders';
import { If } from '../../../components/If';
import MetaTags from '../../../components/Seo/MetaTags';
import { TabSection } from '../../../components/TabSection';
import OperationRelatedArticles from '../OperationRelatedArticles';
import { OperationTimeline } from '../OperationTimeline';

export default function OperationDebentures() {
  const { tab, hashId, market, slug } = useParams<{
    tab: string;
    hashId: string;
    market: string;
    slug: string;
  }>();
  const history = useHistory();
  const [selectedTab, setSelectedTab] = useState(() => tab ?? 'resumo');
  const [error, setError] = useState<Error | null>(null);
  const tabs = [
    { label: 'Resumo', value: 'resumo' },
    { label: 'Dados Gerais', value: 'dados' },
    { label: 'Prestadores de Serviço', value: 'prestadores' },
    { label: 'Ativo', value: 'ativo' },
    { label: 'Passivo', value: 'passivo' },
    { label: 'Linha do Tempo', value: 'timeline' },
    { label: 'Artigos Relacionados', value: 'artigos' },
  ];

  const marketDisplayLabel = getMarketDisplayLabel(market);

  const handleTabChange = (tabIndex: string) => {
    setSelectedTab(tabIndex);
    if (tabIndex === 'resumo') {
      history.push(`/operacao/${market}/${slug}/${hashId}`);
    } else {
      history.push(`/operacao/${market}/${slug}/${hashId}/${tabIndex}`);
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['debenturesResume', hashId],
    queryFn: () => getDebenturesResumeData(hashId),
    enabled: !!hashId,
    retry: false,
    onError: (queryError: AxiosError) => {
      const message =
        (queryError.response?.data as { error: { message?: string } })?.error
          ?.message ?? queryError.message;

      setError(new Error(message));
      toast.error(`Erro ao carregar dados de Debêntures: ${message}`);
    },
  });

  useEffect(() => {
    setSelectedTab(tab ?? 'resumo');
  }, [tab]);

  if (isLoading) return <CircularProgress color="primary" />;

  return (
    <>
      <MetaTags
        title={`${data?.general?.nomeFantasia ?? 'Debêntures'} - Debêntures`}
        description={`Encontre as principais informações sobre ${data?.general?.nomeFantasia ?? 'esta operação'}`}
      />
      <OperationSubheader
        title={data?.general?.nomeFantasia ?? 'Debêntures'}
        market={marketDisplayLabel}
        breadcrumbs1="Fundos"
        breadcrumbs2={`${data?.general?.nomeFantasia ?? 'Debêntures'} ${marketDisplayLabel}`}
        disableAlerts={true}
        entityId={data?.general?.operationId}
      />
      <Box py={2}>
        <TabSection
          tabList={tabs}
          value={selectedTab}
          setValue={handleTabChange}
          isProfile={false}>
          <Paper square>
            <If condition={selectedTab === 'resumo'}>
              <OperationDebenturesResume
                data={data}
                isLoading={isLoading}
                error={error ?? undefined}
              />
            </If>
            <If condition={selectedTab === 'dados'}>
              <OperationDebenturesGeneralData hashId={hashId} />
            </If>
            <If condition={selectedTab === 'prestadores'}>
              <OperationDebenturesServiceProviders hashId={hashId} />
            </If>
            <If condition={selectedTab === 'ativo'}>
              <OperationDebenturesAsset hashId={hashId} />
            </If>
            <If condition={selectedTab === 'passivo'}>
              <OperationDebenturesLiability hashId={hashId} />
            </If>
            <If condition={selectedTab === 'timeline'}>
              <OperationTimeline hashId={hashId} />
            </If>
            <If condition={selectedTab === 'artigos'}>
              <OperationRelatedArticles hash={hashId} />
            </If>
          </Paper>
        </TabSection>
      </Box>
    </>
  );
}
